import { computed, ref, watch } from "vue";
import { useStore } from "vuex";

import { defineStore } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import { buildDocumentEventBase } from "@/helpers/analytics/documents";
import { makeRandomString } from "@/helpers/formatting";
import { useAuth } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";
import { useDealsBase } from "@/hooks/deals";
import applicationsApiService from "@/services/modules/applications";
import { useAnalyticsStore } from "@/stores/analytics";

import type {
  IFile,
  IFilePayload,
  QueuedDocument,
  RequiredDocument,
  UploadedDocument,
} from "@/models/common";
import type { FileTypeCollection } from "@/models/options";
import type { ComputedRef, Ref } from "vue";

export type DocumentsState = {
  canEditAndDelete: ComputedRef<boolean>;
  deleteDocuments: (fileIds: (number | string)[]) => Promise<void>;
  documentIsUploaded: (
    model: UploadedDocument | QueuedDocument
  ) => model is IFile;
  requiredDocuments: Ref<RequiredDocument[]>;
  retryFailedUpload: (fileId: number | string) => Promise<void>;
  tableRows: ComputedRef<(QueuedDocument | UploadedDocument)[]>;
  updateDocument: (
    fileId: IFile["id"],
    updateDocumentPayload: Partial<IFilePayload>
  ) => Promise<void>;
  uploadDocuments: (
    files: FileList,
    options?: Partial<IFilePayload>
  ) => Promise<void>;
  uploadedDocuments: Ref<UploadedDocument[]>;
  getRequiredDocuments: () => Promise<void>;
  getUploadedDocuments: () => Promise<void>;
  gettingUploadedDocs?: Ref<boolean, boolean>;
  queuedDocuments: Ref<QueuedDocument[]>;
  previewDealDocumentFile: Ref<IFile | null>;
  getLabelForFileType: (fileType: string) => string;
  commentedDocuments: ComputedRef<UploadedDocument[]>;
};

export const useDocumentsState = defineStore(
  "documents",
  (): DocumentsState => {
    const { isClientAdmin, isClientFundingAdvisor, isLendflowUser } = useAuth();
    const { commit, getters } = useStore();
    const { activeDeal } = useDealsBase();
    const { trackEvent } = useAnalyticsStore();

    const queuedDocuments = ref<QueuedDocument[]>([]);
    const uploadedDocuments = ref<UploadedDocument[]>([]);
    const requiredDocuments = ref<RequiredDocument[]>([]);
    const previewDealDocumentFile = ref<IFile | null>(null);

    const fileTypes = computed(() =>
      (getters["options/fileTypes"] as FileTypeCollection[])?.flatMap(
        (value) => value.categories
      )
    );

    const commentedDocuments = computed(() => {
      return uploadedDocuments.value?.filter(
        (document) => document.comments?.length
      );
    });

    const { fetchWrapper: getUploadedDocuments, loading: gettingUploadedDocs } =
      usePromiseWrapper(async () => {
        const applicationId = activeDeal.value.id;
        const documents = await applicationsApiService.getFiles(applicationId);

        uploadedDocuments.value = documents;
      });

    const { fetchWrapper: getRequiredDocuments } = usePromiseWrapper(
      async () => {
        const applicationId = activeDeal.value.id;
        const documents = (
          await applicationsApiService.getRequiredFiles(applicationId)
        ).sort((a, b) => {
          const isCompleteA = a.uploaded_count >= a.required_count;
          const isCompleteB = b.uploaded_count >= b.required_count;

          if (isCompleteA !== isCompleteB) {
            return isCompleteA ? 1 : -1;
          }

          return a.weight - b.weight;
        });

        requiredDocuments.value = documents;
      }
    );

    const { fetchWrapper: uploadDocuments } = usePromiseWrapper(
      async (files: FileList, options?: Partial<IFilePayload>) => {
        const applicationId = activeDeal.value.id;

        // Because multiple files can be uploaded as part of the same request, we
        // need a way to know which files were part of which request in the queue.
        const groupId = makeRandomString();

        const serializedFiles: QueuedDocument[] = Object.values(files).map(
          (file) => ({
            id: makeRandomString(),
            original_name: file.name,
            mime_type: file.type,
            file_size: Math.round(file.size / 1024), // in kbytes
            loading: true,
            failed: false,
            created_at: new Date().toISOString(),
            file,
            groupId,
            ...options,
          })
        );

        queuedDocuments.value = [...serializedFiles, ...queuedDocuments.value];

        try {
          const uploadedDocumentsResult =
            await applicationsApiService.uploadMultipleApplicationFiles(
              applicationId,
              serializedFiles
            );
          await getRequiredDocuments();

          queuedDocuments.value = queuedDocuments.value.filter(
            (document) => document.groupId !== groupId
          );
          uploadedDocuments.value = [
            ...uploadedDocumentsResult,
            ...(uploadedDocuments.value ?? []),
          ];
          commit("applications/addFilesToActiveApp", uploadedDocumentsResult);

          for (const uploadedFile of uploadedDocumentsResult) {
            trackEvent(AnalyticsEvent.DashboardDocumentUploaded, {
              ...buildDocumentEventBase(uploadedFile, activeDeal.value),
              document_type: uploadedFile.file_type ?? null,
            });
          }
        } catch {
          queuedDocuments.value = queuedDocuments.value.map(
            (queuedDocument) => ({
              ...queuedDocument,
              loading:
                queuedDocument.groupId === groupId
                  ? false
                  : queuedDocument.loading,
              failed:
                queuedDocument.groupId === groupId
                  ? true
                  : queuedDocument.failed,
            })
          );
        }
      }
    );

    const { fetchWrapper: retryFailedUpload } = usePromiseWrapper(
      async (fileId: number | string) => {
        const applicationId = activeDeal.value.id;
        const fileToRetry = queuedDocuments.value.find(
          (file) => file.id === fileId
        );

        if (!fileToRetry?.failed) {
          return;
        }

        try {
          setDocumentAsLoading(fileId);
          const uploadedDocumentsResult =
            await applicationsApiService.uploadMultipleApplicationFiles(
              applicationId,
              [fileToRetry]
            );

          queuedDocuments.value = queuedDocuments.value.filter(
            (document) => document.id !== fileId
          );
          uploadedDocuments.value = [
            ...uploadedDocumentsResult,
            ...(uploadedDocuments.value ?? []),
          ];
          commit("applications/addFilesToActiveApp", uploadedDocumentsResult);

          for (const uploadedFile of uploadedDocumentsResult) {
            trackEvent(AnalyticsEvent.DashboardDocumentUploaded, {
              ...buildDocumentEventBase(uploadedFile, activeDeal.value),
              document_type: uploadedFile.file_type ?? null,
            });
          }
        } catch {
          fileToRetry.loading = false;
          fileToRetry.failed = true;
        }
      }
    );

    const { fetchWrapper: updateDocument } = usePromiseWrapper(
      async (
        fileId: IFile["id"],
        updateDocumentPayload: Partial<IFilePayload>
      ) => {
        const applicationId = activeDeal.value.id;
        const previousType = uploadedDocuments.value?.find(
          (document) => document.id === fileId
        )?.file_type;
        setDocumentAsLoading(fileId);

        try {
          const result = await applicationsApiService.updateFile(
            applicationId,
            fileId,
            updateDocumentPayload
          );
          await getRequiredDocuments();

          uploadedDocuments.value = uploadedDocuments.value.map((document) =>
            document.id === fileId ? result : document
          );
          commit("applications/setUpdatedFile", result);

          if (result.file_type !== previousType) {
            trackEvent(AnalyticsEvent.DashboardDocumentTypeChanged, {
              ...buildDocumentEventBase(result, activeDeal.value),
              previous_type: previousType ?? null,
              new_type: result.file_type,
            });
          }
        } catch {
          cancelDocumentLoading(fileId);
        }
      }
    );

    const { fetchWrapper: deleteDocuments } = usePromiseWrapper(
      async (fileIds: (number | string)[]) => {
        queuedDocuments.value = queuedDocuments.value.filter(
          (queuedDocument) => !fileIds.includes(queuedDocument.id)
        );

        const ids = fileIds.filter((id) => typeof id === "number");
        fileIds.forEach(setDocumentAsLoading);

        try {
          await applicationsApiService.deleteFiles({ ids });
          await getRequiredDocuments();

          uploadedDocuments.value = uploadedDocuments.value.filter(
            (document) => !fileIds.includes(document.id)
          );
          ids.forEach((id: number) =>
            commit("applications/handleDeletedDocument", id)
          );
        } catch {
          fileIds.forEach(cancelDocumentLoading);
        }
      }
    );

    const tableRows = computed<(QueuedDocument | UploadedDocument)[]>(() => [
      ...queuedDocuments.value,
      ...(uploadedDocuments.value ?? []),
    ]);

    const canEditAndDelete = computed(
      () => isLendflowUser || isClientAdmin || isClientFundingAdvisor
    );

    const setDocumentAsLoading = (fileId: string | number) => {
      const collection =
        typeof fileId === "number" ? uploadedDocuments : queuedDocuments;
      const fileIndex = collection.value.findIndex(
        (document) => document.id === fileId
      );

      if (fileIndex === -1) {
        return;
      }

      collection.value[fileIndex].loading = true;
      collection.value[fileIndex].failed = false;
    };

    const cancelDocumentLoading = (fileId: string | number) => {
      const collection =
        typeof fileId === "number" ? uploadedDocuments : queuedDocuments;
      const fileIndex = collection.value.findIndex(
        (document) => document.id === fileId
      );

      if (fileIndex === -1) {
        return;
      }

      collection.value[fileIndex].loading = false;
    };

    const documentIsUploaded = (
      model: UploadedDocument | QueuedDocument
    ): model is IFile => !model.loading && !model.failed;

    const getLabelForFileType = (fileType: string) => {
      return (
        fileTypes.value?.find((t) => t.type === fileType)?.name ?? fileType
      );
    };

    watch(
      () => activeDeal.value?.id,
      () => {
        if (activeDeal.value?.id) {
          getUploadedDocuments();
          getRequiredDocuments();
        }
      },
      { immediate: true }
    );

    return {
      canEditAndDelete,
      deleteDocuments,
      documentIsUploaded,
      requiredDocuments,
      retryFailedUpload,
      tableRows,
      updateDocument,
      uploadDocuments,
      uploadedDocuments,
      getRequiredDocuments,
      getUploadedDocuments,
      gettingUploadedDocs,
      queuedDocuments,
      previewDealDocumentFile,
      getLabelForFileType,
      commentedDocuments,
    };
  }
);
